import { partialParse } from "openai-partial-json-parser";
import {
  AskAiChatMessageFragmentFragment,
  ChatbotRoleEnum,
  GetAskAiChatSessionQueryQuery,
} from "types/graphql-schema";
import { v4 as uuidv4 } from "uuid";

import { getLabel } from "@apps/use-label/use-label";

import { ChatSessionFunctionCallType } from "./ask-ai";

export const askAIPlaceholderDebounceTime = 100;

export const createChatMessageNode = (
  partialChatMessage: Partial<AskAiChatMessageFragmentFragment>
): AskAiChatMessageFragmentFragment => ({
  __typename: "ChatMessageNode",
  id: uuidv4(),
  role: ChatbotRoleEnum.User,
  content: "",

  error: false,
  autogenerated: false,
  isStreaming: false,

  createdArtifact: null,
  outputItems: [],

  ...partialChatMessage,
});

export const getFunctionCallLabel = ({
  chatSession,
  chatSessionFunctionCall,
}: {
  chatSession: GetAskAiChatSessionQueryQuery["chatSession"];
  chatSessionFunctionCall: ChatSessionFunctionCallType;
}): string | null => {
  if (!chatSessionFunctionCall || !chatSession) {
    return null;
  }
  if (chatSession.id !== chatSessionFunctionCall.chatSessionId) {
    return null;
  }
  const defaultLabel = "Thinking...";
  const label = getLabel();
  const functionName = chatSessionFunctionCall.functionName;
  const parameters = partialParse(chatSessionFunctionCall.parameters);

  if (functionName === "list_artifacts") {
    const artifactType = parameters?.artifact_type;
    if (artifactType === 4) {
      return `Looking at ${label("goal", { pluralize: true })}...`;
    }
    if (artifactType === 1) {
      return `Looking at ${label("action item", { pluralize: true })}...`;
    }
    if (artifactType === 10) {
      return `Looking at ${label("feedback", { pluralize: true })}...`;
    }
    if (artifactType === 9) {
      return `Looking at ${label("recognition", { pluralize: true })}...`;
    }
  } else if (functionName === "list_meetings") {
    return `Looking at meetings`;
  } else if (functionName === "get_user_infos") {
    return `Getting user informations...`;
  } else if (functionName === "query_external_events") {
    const sources = parameters?.sources;
    if (sources === null) {
      return `Looking at integrations activities`;
    }
    if (Array.isArray(sources)) {
      const validSources = (sources || []).reduce((memo, source) => {
        if (source === "github") {
          memo.push(`GitHub`);
        } else if (source === "linear") {
          memo.push(`Linear`);
        } else if (source === "hubspot") {
          memo.push(`HubSpot`);
        } else if (source === "jira") {
          memo.push(`Jira`);
        }
        return memo;
      }, []);
      if (validSources.length > 0) {
        return `Looking at ${validSources.join(", ")} activities`;
      }
    }
  }
  return defaultLabel;
};

export const parsePartialJson = (jsonStr: string) => {
  return partialParse(jsonStr);
};
